import type {
  AnnotationCheckpointV1,
  AnnotationSessionCborV1,
  LabelRasterAnnotationState,
  LabelRasterChunkState,
  LabelRasterRunBlob
} from "@shared/lib/history-types"
import { decode, encode } from "cbor-x"

import { labelRasterRunKey } from "./label-raster-runs"
import { sessionCborSchemaV1 } from "./session-cbor-schema"

/**
 * Wire-format shape of a label raster chunk: runs are replaced by a reference
 * into the session-level run store so identical runs (the common case across
 * undo checkpoints) are stored once.
 */
type WireLabelRasterChunk = Omit<LabelRasterChunkState, "runs"> & { runs?: Uint8Array; runsRef?: string }

function dedupeLabelRasterRuns(checkpoints: AnnotationCheckpointV1[]): {
  checkpoints: AnnotationCheckpointV1[]
  runStore: LabelRasterRunBlob[]
} {
  const store = new Map<string, Uint8Array>()
  const byArray = new WeakMap<Uint8Array, string>()
  let any = false

  const wireCheckpoints = checkpoints.map((cp) => {
    const rasters = cp.snapshot.labelRasters
    if (!rasters || rasters.length === 0) return cp
    any = true
    const wireRasters = rasters.map((raster) => ({
      ...raster,
      chunks: raster.chunks.map((chunk): WireLabelRasterChunk => {
        // Runs are immutable once stored, so identical array instances (shared
        // between checkpoints) hash once; distinct arrays with identical
        // content still collapse to the same key.
        let key = byArray.get(chunk.runs)
        if (!key) {
          key = labelRasterRunKey(chunk.runs)
          byArray.set(chunk.runs, key)
        }
        if (!store.has(key)) store.set(key, chunk.runs)
        const { runs: _runs, ...rest } = chunk
        return { ...rest, runsRef: key }
      })
    }))
    return {
      ...cp,
      snapshot: { ...cp.snapshot, labelRasters: wireRasters as unknown as LabelRasterAnnotationState[] }
    }
  })

  if (!any) return { checkpoints, runStore: [] }
  return {
    checkpoints: wireCheckpoints,
    runStore: [...store.entries()].map(([key, runs]) => ({ key, runs }))
  }
}

export function encodeSessionCbor(session: AnnotationSessionCborV1): Uint8Array {
  const { checkpoints, runStore } = dedupeLabelRasterRuns(session.checkpoints)
  if (runStore.length === 0) {
    const { labelRasterRunStore: _ignored, ...plain } = session
    return encode(plain)
  }
  return encode({ ...session, checkpoints, labelRasterRunStore: runStore })
}

/**
 * Resolve `runsRef` keys back to shared byte arrays. Returns false when a
 * reference cannot be resolved (corrupt file).
 */
function resolveLabelRasterRuns(session: AnnotationSessionCborV1): boolean {
  const store = new Map<string, Uint8Array>()
  for (const blob of session.labelRasterRunStore ?? []) store.set(blob.key, blob.runs)

  for (const cp of session.checkpoints) {
    const rasters = cp.snapshot.labelRasters
    if (!rasters) continue
    for (const raster of rasters) {
      for (const chunk of raster.chunks as WireLabelRasterChunk[]) {
        if (chunk.runs) {
          delete chunk.runsRef
          continue
        }
        const runs = chunk.runsRef ? store.get(chunk.runsRef) : undefined
        if (!runs) return false
        chunk.runs = runs
        delete chunk.runsRef
      }
    }
  }
  delete session.labelRasterRunStore
  return true
}

export function decodeSessionCbor(payload: ArrayBuffer | Uint8Array): AnnotationSessionCborV1 | null {
  try {
    const bytes = payload instanceof Uint8Array ? payload : new Uint8Array(payload)
    const decoded = decode(bytes)
    const result = sessionCborSchemaV1.safeParse(decoded)
    if (!result.success) {
      return null
    }

    const cursorMax = result.data.checkpoints.length - 1
    if (result.data.cursor > cursorMax) {
      return null
    }

    const session = result.data as AnnotationSessionCborV1
    if (!resolveLabelRasterRuns(session)) {
      return null
    }
    return session
  } catch {
    return null
  }
}
